import { useEffect, useRef, useState, useCallback } from "react";

/**
 * Hook simple : un seul stream audio à la fois.
 * Renvoie { playingId, isLoading, toggle(id, streamUrl), stop, volume, setVolume }
 */
export function useSingleAudioPlayer() {
  const audioRef = useRef(null);
  const [playingId, setPlayingId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [volume, setVolumeState] = useState(0.8);

  useEffect(() => {
    if (!audioRef.current) {
      const a = new Audio();
      a.preload = "none";
      a.crossOrigin = "anonymous";
      a.volume = volume;
      audioRef.current = a;
    }
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = "";
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setVolume = useCallback((v) => {
    setVolumeState(v);
    if (audioRef.current) audioRef.current.volume = v;
  }, []);

  const stop = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = "";
    }
    setPlayingId(null);
    setIsLoading(false);
  }, []);

  const toggle = useCallback(
    (id, streamUrl) => {
      const a = audioRef.current;
      if (!a) return;
      if (playingId === id) {
        a.pause();
        a.src = "";
        setPlayingId(null);
        return;
      }
      a.pause();
      const sep = streamUrl.includes("?") ? "&" : "?";
      a.src = `${streamUrl}${sep}_t=${Date.now()}`;
      setIsLoading(true);
      const onPlaying = () => setIsLoading(false);
      const onError = () => {
        setIsLoading(false);
        setPlayingId(null);
      };
      a.onplaying = onPlaying;
      a.onerror = onError;
      a.play().then(() => setPlayingId(id)).catch(() => {
        setIsLoading(false);
        setPlayingId(null);
      });
    },
    [playingId]
  );

  return { playingId, isLoading, toggle, stop, volume, setVolume };
}
